import { ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { useCallback } from "react";
import { STANDARD_TOAST_ERROR, useToastMessage } from "src/contexts";
import { stopSaveVital } from "src/utils/analytics";
import { isDefined } from "src/utils/array.utils";
import { SaveCheckoutConfigInput } from "~generated/graphqlTypes";
import { fail, getDebugParts, useComputed } from "~utils";
import {
  ConfiguratorContext_CheckoutConfigFragment,
  ConfiguratorContext_LoadConfigQueryHookResult,
  useConfiguratorContext_SaveCheckoutConfigMutation,
  useConfiguratorContext_SavePlanChangeMutation,
} from "./Configurator.context.generated";

export type CheckoutFormValue = Omit<SaveCheckoutConfigInput, "optionIds"> & {
  // Keep a local map for better updates, see comment below
  optionRows: { rpogId: string; rpoId: string | undefined }[];
};

type UseCheckoutFormState = {
  formState: ObjectState<CheckoutFormValue>;
  selectedOptionIds: string[];
  setOptionIds: (rpoIds: string[]) => void;
  autoSaving: boolean;
};

export function useCheckoutFormState(
  ccfg: ConfiguratorContext_CheckoutConfigFragment | undefined,
  refetchContextQuery: ConfiguratorContext_LoadConfigQueryHookResult["refetch"],
  rebuildStableOrder: (optionGroups: ConfiguratorContext_CheckoutConfigFragment["optionGroups"]) => void,
): UseCheckoutFormState {
  const [saveConfig, { loading: savingConfig }] = useConfiguratorContext_SaveCheckoutConfigMutation();
  const [savePlanChange, { loading: savingPlanChange }] = useConfiguratorContext_SavePlanChangeMutation();
  const autoSaving = savingConfig || savingPlanChange;
  const { showToast } = useToastMessage();
  const formState = useFormState({
    config,
    init: {
      input: ccfg,
      map: (init) => ({
        productOfferingId: init.productOffering?.id,
        optionRows: init.optionGroups.map((ccog) => ({
          rpogId: ccog.id,
          rpoId: ccog.options.find((cco) => cco.isSelected)?.option.id,
        })),
        ...init,
      }),
    },
    async autoSave(fs) {
      const productOfferingHasChanged = isDefined(fs.changedValue.productOfferingId);
      let planHasChangedElsewhere = false;
      const { optionRows, ...rest } = fs.changedValue;
      // changedValue's optionRows will be incremental updates, so not always have `rpoId` set on every row,
      // so we have to go through `fs.optionRows.value` to get the full list of option IDs.
      const optionIds = optionRows ? fs.optionRows.value.map((row) => row.rpoId).compact() : undefined;

      const onError = async (error: Error) => {
        console.error("Unable to save checkout config", error);
        // This tab is stale with the ccfg previous plans options but hasn't been refreshed by our polling query yet
        // we will inform user of why the plan failed than refetch.
        planHasChangedElsewhere = error.message.includes("Only options from the configured ReadyPlan");
        showToast(planHasChangedElsewhere ? WRONG_PLAN_ERROR : STANDARD_TOAST_ERROR);
        fs.revertChanges();
        stopSaveVital(); // Stop tracking on error
      };

      // Use a separate mutation for plan changes that returns the full context fragment,
      // so Apollo cache updates directly without needing a separate refetch round trip.
      if (productOfferingHasChanged) {
        await savePlanChange({
          variables: { input: { optionIds, ...rest }, debugParts: getDebugParts() },
          onError,
          onCompleted: (data) => rebuildStableOrder(data.saveCheckoutConfig.checkoutConfig.optionGroups),
        });
      } else {
        await saveConfig({ variables: { input: { optionIds, ...rest }, debugParts: getDebugParts() }, onError });
      }

      stopSaveVital(); // Stop tracking on success
      // If the plan was changed in another session, we need to fully refetch all options
      // vs. relying on the mutation response to update the cache.
      if (planHasChangedElsewhere) await refetchContextQuery();
    },
  });

  const selectedOptionIds = useComputed(() => {
    return formState.optionRows.value.map((option) => option.rpoId).compact();
  }, [formState]);

  const setOptionIds = useCallback(
    (rpoIds: string[]) => {
      // Create a rpo -> rpog map, so we can look up the options/rpog row
      const rpoIdToRpogId = ccfg?.optionGroups
        .flatMap((rpog) => rpog.options.map((ccfgo) => ({ rpog, rpo: ccfgo.option })))
        .keyBy(
          (tuple) => tuple.rpo.id,
          (tuple) => tuple.rpog.id,
        );
      // And then rpog -> row
      const rowsByRpogId = formState.optionRows.rows.keyBy((row) => row.rpogId.value!);
      // Now we can do rpo -> rpog -> row to update
      for (const rpoId of rpoIds) {
        const rpogId = rpoIdToRpogId?.[rpoId] ?? fail(`No rpogId found for ${rpoId}`);
        const row = rowsByRpogId[rpogId] ?? fail(`No row found for rpogId ${rpogId}`);
        row.rpoId.value = rpoId;
      }

      // Finally handle deselection of optional options
      for (const row of formState.optionRows.rows) {
        if (row.rpoId.value && !rpoIds.includes(row.rpoId.value)) {
          row.rpoId.value = undefined;
        }
      }
    },
    [ccfg, formState],
  );

  return { formState, selectedOptionIds, setOptionIds, autoSaving };
}

const config: ObjectConfig<CheckoutFormValue> = {
  id: { type: "value" },
  customerEmail: { type: "value" },
  // currently selected plan
  productOfferingId: { type: "value" },
  isReversed: { type: "value" },
  // Use a map of `rpogId => rpoId` so that we can accept incremental updates from the backend,
  // i.e. change `group1=a`, send it to the server, and keep making local updates like `group2=b`,
  // without ruining our ability to accept the server's ack of `group1=a`
  optionRows: {
    type: "list",
    update: "exhaustive",
    config: {
      rpogId: { type: "value", isIdKey: true },
      rpoId: { type: "value" },
    },
  },
};

const WRONG_PLAN_ERROR = {
  title:
    "The plan was changed prior to your selection. Your options have been refreshed. Please review your selections.",
  variant: "error" as const,
  duration: 8000,
};
